// # packages
import * as React from "react";
import { boundMethod } from "autobind-decorator";
import * as _ from "lodash";
// # interfaces
import { iRole } from "@/interfaces/roles";
import { iPermission } from "@/interfaces/permissions";
// # components
import { Spinner } from "@/components/feedback";
import { Modal } from "@/components/modal";
import EditRole from "./EditRole";
// # services
import permissionsApi from "@/services/api/permissions";
import rolesApi from "@/services/api/roles";

interface iState {
  /**
   * ID of the role currently edited.
   */
  editId: null | number;
  flags: {
    /**
     * Whether a role is currently being edited (a modal is open)
     */
    isEditingRole: boolean;
    /**
     * Whether an API request for permissions is in progress.
     */
    isLoadingPermissions: boolean;
    /**
     * Whether an API request for roles is in progress.
     */
    isLoadingRoles: boolean;
  };
  /**
   * The permissions.
   */
  permissions: iPermission[];
  /**
   * The roles.
   */
  roles: iRole[];
}

/**
 * Renders the admin roles scene.
 */
class AdminRolesScene extends React.Component<any, iState> {
  static displayName = "AdminRolesScene";

  state: iState = {
    editId: null,
    flags: {
      isEditingRole: false,
      isLoadingPermissions: false,
      isLoadingRoles: false,
    },
    permissions: [],
    roles: [],
  };

  public componentWillMount() {
    this.fetchPermissions();
    this.fetchRoles();
  }

  /**
   * Renders the content.
   *
   * @returns {React.ReactNode}
   */
  public render(): React.ReactNode {
    return (
      <div className="page-container">
        <h1 className="page-title">Roles</h1>

        <div className="flex mar-bt-20">
          <div className="flex flex-justify-flex-end">
            <a
              className="btn-c2a style-new-item"
              onClick={() => {
                this.openEditRoleModal();
              }}
            >
              <i className="text-icon">+</i>
              New Role
            </a>
          </div>
        </div>

        {this.renderRolesTable()}
        {this.renderEditRoleModal()}
      </div>
    );
  }

  /**
   * Updates state with a new or updated role.
   * New roles will be added, existing roles will be replaced.
   *
   * @param {iRole} role
   * @returns {void}
   */
  @boundMethod
  protected addOrUpdateRole(role: iRole): void {
    const { roles } = this.state;
    const rolesIndex = _.findIndex(roles, { id: role.id });

    this.setState((state) => {
      const roles = [...state.roles];

      if (rolesIndex === -1) {
        // add new role
        roles.push(role);
      } else {
        // update existing role
        roles[rolesIndex] = role;
      }

      return {
        ...state,
        roles,
      };
    });
  }

  /**
   * Closes the edit role modal.
   *
   * @returns {void}
   */
  @boundMethod
  protected closeEditRoleModal(): void {
    this.setState((state) => ({
      ...state,
      flags: {
        ...state.flags,
        isEditingRole: false,
      },
    }));
  }

  /**
   * Opens an edit role modal.
   *
   * @param {(null | number)=} id ID of role to edit. null for new role.
   * @returns {void}
   */
  protected openEditRoleModal(id?: null | number): void {
    this.setState((state) => ({
      ...state,
      flags: {
        ...state.flags,
        isEditingRole: true,
      },
      editId: id,
    }));
  }

  /**
   * Fetches permissions from the API and caches to local state.
   *
   * @returns {Promise<void>}
   */
  protected async fetchPermissions(): Promise<void> {
    this.setState((state) => {
      const stateCopy = { ...state };
      stateCopy.flags.isLoadingPermissions = true;
      return stateCopy;
    });

    const permissions = await permissionsApi.get();

    this.setState((state) => {
      const stateCopy = { ...state };
      stateCopy.flags.isLoadingPermissions = false;
      stateCopy.permissions = permissions;
      return stateCopy;
    });
  }

  /**
   * Fetches roles from the API and caches to local state.
   *
   * @returns {Promise<void>}
   */
  protected async fetchRoles(): Promise<void> {
    try {
      this.setState((state) => {
        const stateCopy = { ...state };
        stateCopy.flags.isLoadingRoles = true;
        return stateCopy;
      });

      const roles = await rolesApi.query();

      this.setState((state) => {
        const stateCopy = { ...state };
        stateCopy.flags.isLoadingRoles = false;
        stateCopy.roles = roles;
        return stateCopy;
      });
    } catch (err) {
      this.setState((state) => {
        const stateCopy = { ...state };
        stateCopy.flags.isLoadingRoles = false;
        return stateCopy;
      });

      return Promise.reject(err);
    }
  }

  /**
   * Whether to block the UI (an API request is currently in progress)
   *
   * @returns {boolean}
   */
  protected isUISuspended(): boolean {
    const { flags } = this.state;
    return flags.isLoadingPermissions || flags.isLoadingRoles;
  }

  /**
   * Removes a role from state.roles
   * Closes the modal if this role was being edited.
   *
   * @param {iRole} role
   * @returns {void}
   */
  @boundMethod
  protected removeRole(role: iRole): void {
    this.setState((state) => ({
      ...state,
      // clear reference to the role being edited
      editId: null,
      flags: {
        ...state.flags,
        // close the modal
        isEditingRole: false,
      },
      // filter the removed role out of the list
      roles: state.roles.filter((item) => item.id !== role.id),
    }));
  }

  /**
   * Renders the edit role modal if required.
   *
   * @returns {React.ReactNode}
   */
  @boundMethod
  protected renderEditRoleModal(): React.ReactNode {
    const { editId, flags, roles } = this.state;

    // don't render modal if we're not in editing mode
    if (!flags.isEditingRole) {
      return null;
    }

    // grab the role to be edited out of component state
    const selectedRole = editId ? _.find(roles, { id: editId }) : null;

    return (
      <Modal
        title={editId ? "Edit Role" : "New Role"}
        onClose={this.closeEditRoleModal}
      >
        <EditRole
          onDelete={this.removeRole}
          onSave={this.addOrUpdateRole}
          permissions={this.state.permissions}
          role={selectedRole}
        />
      </Modal>
    );
  }

  /**
   * Renders a table of permissions.
   *
   * @returns {React.ReactNode}
   */
  protected renderRolesTable(): React.ReactNode {
    const { flags, roles } = this.state;

    return (
      <div className="content-container">
        {this.isUISuspended() ? <Spinner /> : null}

        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {roles.map((role) => (
                <tr>
                  <td>{role.label}</td>
                  <td>
                    <a
                      onClick={this.openEditRoleModal.bind(this, role.id)}
                      className="btn-c2a style-secondary"
                    >
                      Edit
                    </a>
                  </td>
                </tr>
              ))}

              {!roles.length ? (
                <tr>
                  <td colSpan={2}>
                    <p className="text-muted text-center">No roles found</p>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

export default AdminRolesScene;
